import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ChevronLeft, Package, type LucideIconData } from 'lucide-angular';
import { Observable, catchError, map, of, startWith, switchMap } from 'rxjs';

import { AuthService } from '../../../../shared/services/auth.service';
import { BadgeComponent, ButtonComponent, IconComponent } from '../../../../shared/ui';
import { formatCrc, formatInteger } from '../../../../shared/utils/currency.util';
import { productImageUrl } from '../../../../shared/utils/product.utils';
import type { ProfileOrderDetail, ProfileOrderLayaway } from '../../data/profile-orders.models';
import { ProfileOrdersService, type ProfileOrdersFailure } from '../../data/profile-orders.service';
import { PROFILE_COPY } from '../../profile.copy';
import { formatIsoDate } from '../../utils/profile-format.util';
import { orderStatusVariant, ordersBlockedByVerification } from '../../utils/profile-orders.util';
import { ProfileOrdersUnverifiedComponent } from '../profile-orders-unverified/profile-orders-unverified.component';

type DetailState =
  | { status: 'signed-out' }
  | { status: 'blocked' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'not-found'; message: string }
  | { status: 'loaded'; order: ProfileOrderDetail };

interface DetailKey {
  userId: number | null;
  blocked: boolean;
  id: string | null;
  attempt: number;
}

const sameKey = (a: DetailKey, b: DetailKey): boolean =>
  a.userId === b.userId && a.blocked === b.blocked && a.id === b.id && a.attempt === b.attempt;

/**
 * La API solo enruta ids numéricos (`whereNumber`): cualquier otra cosa no se pide. Hasta 18 dígitos, y la URL se
 * construye con la cadena, nunca con `Number(id)`: por encima de 2^53 `Number` redondea (`9007199254740993` →
 * `…992`) y pediría OTRO pedido (Astra W1).
 */
const NUMERIC_ID = /^\d{1,18}$/;

/**
 * Detalle de un pedido (`/perfil/pedidos/:id`, W1, §5.6.3 de B10): líneas, entrega, totales y los bloques de pago
 * por separado (B7). Solo lectura: sin botones de pagar, cancelar ni subir comprobante.
 *
 * Atado a la cuenta como el listado: un detalle abierto de A que pasa a B se vuelve a pedir con la sesión de B (→ su
 * 404), y la respuesta en vuelo de A se descarta (`switchMap`).
 */
@Component({
  selector: 'app-profile-order-detail',
  imports: [BadgeComponent, ButtonComponent, IconComponent, ProfileOrdersUnverifiedComponent, RouterLink],
  templateUrl: './profile-order-detail.component.html',
  styleUrl: './profile-order-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileOrderDetailComponent {
  private readonly orders = inject(ProfileOrdersService);
  private readonly currentUser = inject(AuthService).currentUser;

  protected readonly copy = PROFILE_COPY.ORDER_DETAIL;
  protected readonly loadCopy = PROFILE_COPY.ORDERS;
  protected readonly packageIcon: LucideIconData = Package;
  protected readonly backIcon: LucideIconData = ChevronLeft;
  protected readonly formatCrc = formatCrc;
  protected readonly formatIsoDate = formatIsoDate;
  /**
   * Líneas cuya imagen no cargó, por posición y atadas al objeto del pedido pintado. Otro `:id`, otra cuenta o un
   * reintento traen un objeto nuevo y empiezan limpios. Dos líneas con la misma `image` no se arrastran (Astra W1 p2).
   */
  private readonly failedLines = signal<{ order: ProfileOrderDetail | null; lines: ReadonlySet<number> }>({
    order: null,
    lines: new Set(),
  });
  protected readonly statusVariant = orderStatusVariant;

  private readonly id = toSignal(inject(ActivatedRoute).paramMap.pipe(map((p) => p.get('id'))), {
    initialValue: null,
  });
  private readonly attempt = signal(0);

  private readonly key = computed<DetailKey>(
    () => ({
      userId: this.currentUser()?.id ?? null,
      blocked: ordersBlockedByVerification(this.currentUser()),
      id: this.id(),
      attempt: this.attempt(),
    }),
    { equal: sameKey },
  );

  protected readonly state = toSignal(toObservable(this.key).pipe(switchMap((key) => this.load(key))), {
    initialValue: { status: 'loading' } as DetailState,
  });

  protected readonly order = computed(() => {
    const state = this.state();
    return state.status === 'loaded' ? state.order : null;
  });

  protected readonly notFoundMessage = computed(() => {
    const state = this.state();
    return state.status === 'not-found' ? state.message : null;
  });

  protected retry(): void {
    this.attempt.update((n) => n + 1);
  }

  /** ¿Hay algo que pintar en «Pago»? Si no, el bloque entero se omite. */
  protected hasPayment(order: ProfileOrderDetail): boolean {
    const p = order.payment;
    return !!(p.method || p.receipts.length || p.online_status || p.proof_received || p.layaway || p.note);
  }

  /** Apartado vivo (§7.2): verificando · saldo pendiente · sin abono registrado (texto aprobado el 30/09/2026). */
  protected layawayText(layaway: ProfileOrderLayaway): string {
    const due = formatIsoDate(layaway.due_date);
    if (layaway.verifying) return this.copy.LAYAWAY_VERIFYING.replace('{dd/mm/aaaa}', due);
    if (layaway.pending !== null) {
      return this.copy.LAYAWAY_PENDING.replace('{pending}', formatInteger(layaway.pending)).replace('{dd/mm/aaaa}', due);
    }
    return this.copy.LAYAWAY_NO_PAYMENT.replace('{dd/mm/aaaa}', due);
  }

  /** URL de la imagen de una línea, o `null` (sin fichero o esa línea ya falló al cargar) → caja con `Package`. */
  protected lineImage(order: ProfileOrderDetail, index: number, image: string | null): string | null {
    const src = productImageUrl(image);
    if (!src) return null;
    const failed = this.failedLines();
    return failed.order === order && failed.lines.has(index) ? null : src;
  }

  protected onImageError(order: ProfileOrderDetail, index: number): void {
    this.failedLines.update((failed) => ({
      order,
      lines: new Set(failed.order === order ? failed.lines : []).add(index),
    }));
  }

  protected quantityLabel(quantity: number): string {
    return this.copy.QUANTITY.replace('{n}', String(quantity));
  }

  private load(key: DetailKey): Observable<DetailState> {
    if (key.userId === null) return of({ status: 'signed-out' });
    if (key.blocked) return of({ status: 'blocked' });
    if (!key.id || !NUMERIC_ID.test(key.id)) return of({ status: 'not-found', message: this.copy.NOT_FOUND });

    return this.orders.detail(key.id).pipe(
      map((order): DetailState => ({ status: 'loaded', order })),
      catchError((failure: ProfileOrdersFailure) =>
        of<DetailState>(
          failure?.kind === 'not-found' ? { status: 'not-found', message: failure.message } : { status: 'error' },
        ),
      ),
      startWith<DetailState>({ status: 'loading' }),
    );
  }
}
